// Generic engine behind every money-moving super-admin dialog (contract §4.1's six gated
// endpoints: payout approve/reject, refund, tenant adjustment, dispute recovery, settings
// save). `PayoutApproveDialog` and `PayoutRejectDialog` (pinned by contract §9) are thin
// wrappers around this; the other four gated actions use it directly inside
// `GatewayConsole.jsx` rather than each inventing their own copy of the same three steps:
//
//   1. form   — the money summary (`children`) + an acknowledgement checkbox and/or a
//               reason textarea, validated before the user can go further.
//   2. verify — "confirm your identity" (`StepUpAuth`), embedded in THIS SAME dialog, never
//               a second modal stack.
//   3. submit — `onConfirm({reason, stepUpToken})` runs automatically the instant identity
//               verification resolves. A `REAUTH_REQUIRED` comeback (token raced/expired)
//               re-shows step 2 with an inline error, never a bare toast.
//
// Zero native popups: Radix `Dialog` only.
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import { useT } from "../../lib/LanguageContext";
import { gatewayErrorText } from "../errorText";
import StepUpAuth from "./StepUpAuth";

/**
 * @param {boolean} open
 * @param {function} onClose (didMutate: boolean) => void
 * @param {string} titleKey / {object} [titleVars]
 * @param {string} [descKey]
 * @param {boolean} [requireAck=true]
 * @param {string} [ackKey] i18n key for the acknowledgement label
 * @param {object} [ackVars] placeholder values for that label ("{tenant}", "{amount}")
 * @param {boolean} [requireReason=false]
 * @param {number} [reasonMinLen=5]
 * @param {string} [reasonPlaceholderKey]
 * @param {function} onConfirm async ({reason, stepUpToken}) => {ok, code}
 * @param {import("react").ReactNode} [children] the money summary shown above the controls
 * @param {object} [gatewayApi] threaded straight into StepUpAuth (DI seam, contract §9)
 * @param {string} [stepUpPromptKey] threaded into StepUpAuth's `promptKey` (see there)
 */
export default function StepUpConfirmDialog({
  open,
  onClose,
  titleKey,
  titleVars,
  descKey,
  requireAck = true,
  ackKey,
  ackVars,
  requireReason = false,
  reasonMinLen = 5,
  reasonPlaceholderKey,
  onConfirm,
  children,
  gatewayApi,
  stepUpPromptKey,
}) {
  const t = useT("superadmin");
  const tc = useT("common");
  const [step, setStep] = useState("form"); // form | verify | submitting
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const [submitError, setSubmitError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (open) {
      setStep("form");
      setAck(false);
      setReason("");
      setSubmitError(null);
      setAttempt(0);
    }
  }, [open]);

  const reasonValid = !requireReason || reason.trim().length >= reasonMinLen;
  const ackValid = !requireAck || ack;
  const canContinue = reasonValid && ackValid;

  const runAction = async (stepUpToken) => {
    setStep("submitting");
    setSubmitError(null);
    const result = await onConfirm({ reason: reason.trim(), stepUpToken });
    if (result?.ok) {
      onClose(true);
      return;
    }
    // A REAUTH_REQUIRED comeback (the token raced/expired between mint and use) re-shows
    // the SAME step rather than a toast — that is the point of this component existing.
    //
    // But `code` is null for everything `extractGatewayErrorCode` cannot read: a network
    // drop, a 500 with an HTML body, a 422 pydantic array. Defaulting those to
    // REAUTH_REQUIRED told the operator their identity check had failed and invited them to
    // verify again — blaming them for a server fault, and sending them round a loop that
    // cannot succeed. `null` reaches `gatewayErrorText` as an unknown code and renders
    // `gw.error.UNKNOWN`, which is the honest answer: something went wrong and we cannot say
    // what. The step-up round trip is still re-offered, because the token IS spent either way.
    setSubmitError(result?.code ?? null);
    setStep("verify");
    setAttempt((n) => n + 1);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent data-testid="stepup-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{t(titleKey, titleVars)}</DialogTitle>
          {descKey && <DialogDescription>{t(descKey)}</DialogDescription>}
        </DialogHeader>

        {children}

        {step === "form" && (
          <>
            {requireReason && (
              <div className="space-y-1">
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={reasonPlaceholderKey ? t(reasonPlaceholderKey) : ""}
                  rows={3}
                  data-testid="stepup-reason"
                />
                {!reasonValid && reason.length > 0 && (
                  <p className="text-xs text-rose-600" role="alert" data-testid="stepup-reason-error">
                    {t("gw.console.dlg.reasonTooShort", { min: reasonMinLen })}
                  </p>
                )}
              </div>
            )}
            {requireAck && (
              <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
                <Checkbox
                  checked={ack}
                  onCheckedChange={(v) => setAck(v === true)}
                  className="mt-0.5"
                  data-testid="stepup-ack"
                />
                {ackKey ? t(ackKey, ackVars) : t("gw.console.dlg.genericAck")}
              </label>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => onClose(false)} data-testid="stepup-cancel">{t("gw.console.dlg.back")}</Button>
              <Button onClick={() => setStep("verify")} disabled={!canContinue} data-testid="stepup-continue">
                {t("gw.console.dlg.continue")}
              </Button>
            </DialogFooter>
          </>
        )}

        {(step === "verify" || step === "submitting") && (
          <>
            {submitError && (
              <p className="text-xs text-rose-600" role="alert" data-testid="stepup-confirm-error">
                {gatewayErrorText(submitError, tc)}
              </p>
            )}
            <StepUpAuth key={attempt} onVerified={runAction} gatewayApi={gatewayApi} promptKey={stepUpPromptKey} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("form")} disabled={step === "submitting"}>
                {t("gw.console.dlg.back")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
